import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Switch from "@mui/material/Switch";
import CustomPagination from "../../../utils/paginationUtils";
import editIcon from "../../../assets/edit-icon.svg";
import eyeIcon from "../../../assets/eye.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import CLOSE from "../../../assets/CLOSE.svg";
import closeIcon from "../../../assets/crossIcon.svg";
import downIcon from "../../../assets/down-icon.svg";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

/** Trim, lowercase, collapse whitespace — for tolerant speciality name search */
function normalizeSpecialitySearch(s) {
  return String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/** Match selected value to dropdown option (id first, then normalized name) */
function isSameSpecialtyOption(selected, option) {
  if (!selected || !option) return false;
  const sid = selected.specialityId ?? selected.id;
  const oid = option.specialityId ?? option.id;
  if (sid != null && oid != null && String(sid) === String(oid)) return true;
  const sName = normalizeSpecialitySearch(selected.speciality ?? selected.name ?? "");
  const oName = normalizeSpecialitySearch(option.speciality ?? option.name ?? "");
  return sName.length > 0 && sName === oName;
}

export default function DefaultProcedureList() {
  const location = useLocation();
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelType, setPanelType] = useState("view"); // "add" | "edit" | "view"
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentProcedure, setCurrentProcedure] = useState(null);
  const searchDebounceRef = useRef(null);

  // Add form state
  const [procedureId, setProcedureId] = useState("");
  const [procedure, setProcedure] = useState("");
  const [specialityId, setSpecialityId] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initialEditValues, setInitialEditValues] = useState(null);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [rowForInactive, setRowForInactive] = useState(null);
  const [isStratification, setIsStratification] = useState(false);
  const [isImplant, setIsImplant] = useState(false);

  const fetchProcedures = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ProcedureFlowServices.getDefaultProcedureListV2({
        page,
        limit: PAGE_SIZE,
        ...(searchKey.trim() && { searchKey: searchKey.trim() }),
      });
      const list = Array.isArray(res?.data?.list) ? res.data.list : [];
      const total = res?.data?.total ?? list.length;
      setTotalCount(total);
      setProcedures(
        list.map((item) => ({
          id: item.id,
          procedureId: item.procedureId,
          procedure: (item.procedure ?? "").trim(),
          specialityId: item.specialityId ?? "",
          speciality: (item.speciality ?? "").trim(),
          procedureType: item.procedureType,
          status: item.status ?? "ACTIVE",
          isStratification: item.isStratification === true,
          isImplant: item.isImplant === true,
        }))
      );
      setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
    } catch (err) {
      console.error("Failed to fetch procedures", err);
      setError("Failed to load procedures. Please try again.");
      setProcedures([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcedures();
  }, [page, searchKey]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKey(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const fetchSpecialties = async () => {
    if (specialties.length > 0) return;
    try {
      setLoadingSpecialties(true);
      const res = await ProcedureFlowServices.getSpecialityTypes({
        limit: 9999,
      });
      setSpecialties(Array.isArray(res?.data?.list) ? res.data.list : []);
    } catch (err) {
      setSpecialties([]);
    } finally {
      setLoadingSpecialties(false);
    }
  };

  // Procedure ID search & auto-fill (add mode only)
  useEffect(() => {
    if (!panelVisible || panelType !== "add" || !procedureId.trim()) {
      if (panelType === "add" && !procedureId.trim()) {
        setProcedure("");
        setSpecialityId("");
        setSelectedSpecialty(null);
      }
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await ProcedureFlowServices.searchProcedureById(
          procedureId.trim()
        );
        const list = res?.data?.list || [];
        const matched = list.find((item) => item.procedureId === procedureId.trim());
        if (matched) {
          setProcedure((matched.procedure ?? "").trim());
          setSpecialityId(matched.specialityId ?? "");
          setSelectedSpecialty({
            id: matched.specialityId,
            specialityId: matched.specialityId,
            speciality: (matched.speciality ?? "").trim(),
          });
        } else {
          setProcedure("");
          setSpecialityId("");
          setSelectedSpecialty(null);
        }
      } catch (err) {
        setProcedure("");
        setSpecialityId("");
        setSelectedSpecialty(null);
      }
    }, 600);
    return () => clearTimeout(timeout);
  }, [panelVisible, panelType, procedureId]);

  const openAdd = () => {
    setPanelVisible(true);
    setPanelType("add");
    setCurrentProcedure(null);
    setProcedureId("");
    setProcedure("");
    setSpecialityId("");
    setSelectedSpecialty(null);
    setInitialEditValues(null);
    setIsStratification(false);
    setIsImplant(false);
  };

  // Open add/edit panel when navigated from ManageProcedurePage
  useEffect(() => {
    if (location.state?.openAdd) {
      openAdd();
      window.history.replaceState({}, document.title, location.pathname);
    }
    if (location.state?.openEdit && location.state?.procedure) {
      openEdit(location.state.procedure);
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, []);

  const openEdit = (proc) => {
    setPanelVisible(true);
    setPanelType("edit");
    setCurrentProcedure(proc);
    setProcedureId(proc.procedureId ?? "");
    setProcedure(proc.procedure ?? "");
    setSpecialityId(proc.specialityId ?? "");
    setSelectedSpecialty(
      proc.specialityId || proc.speciality
        ? {
            id: proc.specialityId,
            specialityId: proc.specialityId,
            speciality: proc.speciality ?? "",
          }
        : null
    );
    setIsStratification(proc.isStratification === true);
    setIsImplant(proc.isImplant === true);
    setInitialEditValues({
      procedure: proc.procedure ?? "",
      specialityId: proc.specialityId ?? "",
      specialityName: proc.speciality ?? "",
      isStratification: proc.isStratification === true,
      isImplant: proc.isImplant === true,
    });
  };

  const openView = (proc) => {
    setPanelVisible(true);
    setPanelType("view");
    setCurrentProcedure(proc);
  };

  const closePanel = () => {
    setPanelVisible(false);
    setCurrentProcedure(null);
    setInitialEditValues(null);
  };

  const handleAddProcedure = async () => {
    const pid = procedureId.trim();
    const proc = procedure.trim();
    const sid = specialityId.trim();
    const specName = selectedSpecialty?.speciality?.trim();
    if (!pid || !proc || !sid || !specName) {
      toast.warn("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await ProcedureFlowServices.addProcedure({
        procedureId: pid,
        procedure: proc,
        specialityId: sid,
        speciality: specName,
        isStratification,
        isImplant,
      });
      toast.success("Procedure added successfully.");
      closePanel();
      fetchProcedures();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add procedure.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProcedure = async () => {
    const proc = procedure.trim();
    const sid = specialityId.trim();
    const specName = selectedSpecialty?.speciality?.trim();
    if (!currentProcedure?.procedureId || !proc || !sid || !specName) {
      toast.warn("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await ProcedureFlowServices.addProcedure({
        procedureId: currentProcedure.procedureId,
        procedure: proc,
        specialityId: sid,
        speciality: specName,
        isStratification,
        isImplant,
      });
      toast.success("Procedure updated successfully.");
      closePanel();
      fetchProcedures();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update procedure.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasEditChanges =
    initialEditValues &&
    (procedure.trim() !== initialEditValues.procedure ||
      specialityId !== initialEditValues.specialityId ||
      (selectedSpecialty?.speciality ?? "") !== initialEditValues.specialityName ||
      isStratification !== initialEditValues.isStratification ||
      isImplant !== initialEditValues.isImplant);

  const handleToggleClick = async (proc) => {
    if (proc.status === "ACTIVE") {
      setRowForInactive(proc);
      setInactiveModalOpen(true);
      return;
    }
    try {
      await ProcedureFlowServices.addProcedure({
        id: proc.id,
        status: "ACTIVE",
      });
      toast.success("Procedure activated.");
      fetchProcedures();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to activate.");
    }
  };

  const handleConfirmInactive = async () => {
    if (!rowForInactive?.id) {
      setInactiveModalOpen(false);
      setRowForInactive(null);
      return;
    }
    try {
      await ProcedureFlowServices.addProcedure({
        id: rowForInactive.id,
        status: "INACTIVE",
      });
      toast.success("Procedure inactivated.");
      fetchProcedures();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to inactive.");
    } finally {
      setInactiveModalOpen(false);
      setRowForInactive(null);
    }
  };

  return (
    <div className="w-full min-h-screen min-h-[100dvh] flex flex-col">
      <div className="bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex-1 min-h-full w-full pt-6 pb-3 px-3 sm:pt-8 sm:pb-4 sm:px-4 md:pt-12 md:pb-6 md:px-6">
        {/* HEADER: Title, Search, Add Button */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6 py-4 sm:py-6 md:py-8 mb-4 sm:mb-6">
          <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] align-middle text-black shrink-0">
            Master Procedure List ({totalCount})
          </h1>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 flex-1 lg:flex-initial lg:max-w-[720px]">
            <div className="relative flex-1 min-w-0">
              <img
                src={searchIcon}
                alt="search"
                className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Procedure Name / Procedure ID"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full h-12 sm:h-14 pl-11 sm:pl-14 pr-5 sm:pr-6 border border-solid border-[#E0E0E0] rounded-[54px] outline-none font-lufga text-sm sm:text-base text-[#1B1B1B] placeholder:text-gray-400 bg-white"
              />
            </div>

            <button
              onClick={openAdd}
              className="font-lufga w-[237px] h-[60px] rounded-[54px] gap-[10px] px-6 border-2 border-solid border-[#5AC69F] bg-[#5AC69FCC] flex items-center justify-center font-medium text-black hover:opacity-90 transition-opacity shrink-0"
            >
              <span className="text-xl leading-none">+</span>
              Add New Procedure
            </button>
          </div>
        </div>

        {/* MAIN AREA: Table */}
        <div
          className={`grid gap-6 transition-all duration-300 ${
            panelVisible ? "lg:grid-cols-[1fr_minmax(30vw,35vw)]" : "grid-cols-1"
          }`}
        >
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-x-auto p-4 sm:p-6 min-w-0">
            <div className="grid grid-cols-[56px_100px_minmax(120px,1fr)_120px_minmax(200px,3fr)_120px] gap-3 min-w-[960px] px-0 sm:px-2 py-3 font-lufga font-semibold text-sm sm:text-base text-[#1B1B1B] border-b border-[#E0E0E0]">
              <span>SNo.</span>
              <span>Speciality ID</span>
              <span>Speciality Name</span>
              <span>Procedure ID</span>
              <span>Procedure Name</span>
              <span className="text-right pr-2">Actions</span>
            </div>

            <div className="font-lufga divide-y divide-[#E8E8E8]">
              {loading ? (
                <div className="py-12 text-center text-gray-500 font-lufga">
                  Loading procedures...
                </div>
              ) : error ? (
                <div className="py-12 text-center text-red-600 font-lufga">
                  {error}
                </div>
              ) : procedures.length === 0 ? (
                <div className="py-12 text-center text-gray-500 font-lufga">
                  No procedures found.
                </div>
              ) : (
                procedures.map((proc, index) => (
                  <ProcedureRow
                    key={proc.id ?? proc.procedureId ?? index}
                    index={(page - 1) * PAGE_SIZE + index}
                    procedure={proc}
                    onEdit={() => openEdit(proc)}
                    onView={() => openView(proc)}
                    onToggle={() => handleToggleClick(proc)}
                    alternateBg={index % 2 === 1}
                  />
                ))
              )}
            </div>

            {!loading && !error && procedures.length > 0 && (
              <div className="flex justify-center mt-6 mb-4 sm:mb-6">
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL: Add / Edit / View */}
          {panelVisible && (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-lufga text-[32px] font-normal">
                  {panelType === "add"
                    ? "Add Procedure"
                    : panelType === "edit"
                      ? "Edit Procedure"
                      : "View Procedure"}
                </h2>
                <img
                  src={CLOSE}
                  alt="close"
                  onClick={closePanel}
                  className="w-6 h-6 cursor-pointer"
                />
              </div>

              {panelType === "add" && (
                <AddProcedureForm
                  procedureId={procedureId}
                  setProcedureId={setProcedureId}
                  procedure={procedure}
                  setProcedure={setProcedure}
                  specialityId={specialityId}
                  setSpecialityId={setSpecialityId}
                  selectedSpecialty={selectedSpecialty}
                  setSelectedSpecialty={setSelectedSpecialty}
                  specialties={specialties}
                  loadingSpecialties={loadingSpecialties}
                  fetchSpecialties={fetchSpecialties}
                  isStratification={isStratification}
                  setIsStratification={setIsStratification}
                  isImplant={isImplant}
                  setIsImplant={setIsImplant}
                  onSubmit={handleAddProcedure}
                  submitting={submitting}
                />
              )}

              {panelType === "edit" && (
                <EditProcedureForm
                  procedureId={procedureId}
                  procedure={procedure}
                  setProcedure={setProcedure}
                  specialityId={specialityId}
                  setSpecialityId={setSpecialityId}
                  selectedSpecialty={selectedSpecialty}
                  setSelectedSpecialty={setSelectedSpecialty}
                  specialties={specialties}
                  loadingSpecialties={loadingSpecialties}
                  fetchSpecialties={fetchSpecialties}
                  isStratification={isStratification}
                  setIsStratification={setIsStratification}
                  isImplant={isImplant}
                  setIsImplant={setIsImplant}
                  onSubmit={handleUpdateProcedure}
                  submitting={submitting}
                  hasChanges={hasEditChanges}
                />
              )}

              {panelType === "view" && currentProcedure && (
                <div className="font-lufga text-gray-700 space-y-4">
                  <div>
                    <strong>Speciality ID:</strong>{" "}
                    <span className="text-[#1B1B1B] font-normal">
                      {currentProcedure.specialityId ?? "—"}
                    </span>
                  </div>
                  <div>
                    <strong>Speciality Name:</strong>{" "}
                    <span className="text-[#1B1B1B] font-normal break-words">
                      {currentProcedure.speciality ?? "—"}
                    </span>
                  </div>
                  <div>
                    <strong>Procedure ID:</strong>{" "}
                    <span className="text-[#1B1B1B] font-normal">
                      {currentProcedure.procedureId ?? "—"}
                    </span>
                  </div>
                  <div>
                    <strong>Procedure Name:</strong>{" "}
                    <span className="text-[#1B1B1B] font-normal break-words">
                      {currentProcedure.procedure ?? "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Inactive confirmation modal */}
      {inactiveModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-xl px-8 py-8">
            <button
              onClick={() => {
                setInactiveModalOpen(false);
                setRowForInactive(null);
              }}
              className="absolute top-6 right-6 bg-transparent border-none p-0 cursor-pointer"
            >
              <img src={closeIcon} alt="close" className="w-5 h-5" />
            </button>
            <p className="text-center font-lufga font-medium text-gray-800 text-lg mt-2 pr-8">
              Do you want to inactive this procedure?
            </p>
            <div className="flex justify-center mt-8">
              <button
                onClick={handleConfirmInactive}
                className="rounded-[40px] px-8 py-4 min-h-[56px] border-2 border-solid border-[#5AC69F] bg-[#5AC69F] hover:opacity-90 font-lufga font-semibold text-white transition-opacity"
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProcedureRow({ index, procedure, onEdit, onView, onToggle, alternateBg }) {
  const isActive = procedure.status !== "INACTIVE";

  return (
    <div
      className={`grid grid-cols-[56px_100px_minmax(120px,1fr)_120px_minmax(200px,3fr)_120px] gap-3 min-w-[960px] px-0 sm:px-2 py-4 sm:py-5 items-center font-lufga ${
        alternateBg ? "bg-[#E8E8E833]" : ""
      }`}
    >
      <span className="font-medium text-sm sm:text-base text-[#1B1B1B]">
        {index + 1}
      </span>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {procedure.specialityId ?? "—"}
      </p>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {procedure.speciality ?? "—"}
      </p>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {procedure.procedureId ?? "—"}
      </p>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {procedure.procedure ?? "—"}
      </p>
      <div className="flex items-center gap-3 sm:gap-4 justify-end pr-2">
        <img
          src={eyeIcon}
          alt="view"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onView}
        />
        <img
          src={editIcon}
          alt="edit"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onEdit}
        />
        <Switch
          checked={isActive}
          onChange={onToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#5AC69F",
              "& + .MuiSwitch-track": { backgroundColor: "#5AC69F !important", opacity: 0.6 },
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#9e9e9e !important",
            },
          }}
        />
      </div>
    </div>
  );
}

function ProcedureIdAutocomplete({ value, onChange, onSelect, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (!value.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await ProcedureFlowServices.getDefaultProcedureListV2({
          searchKey: value.trim(),
          limit: 20,
          page: 1,
        });
        const list = Array.isArray(res?.data?.list) ? res.data.list : [];
        setSuggestions(list);
        setOpen(list.length > 0);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  useEffect(() => {
    if (open && suggestions.length > 0 && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }, [open, suggestions.length]);

  useEffect(() => {
    const h = (e) => {
      if (
        inputRef.current &&
        dropdownRef.current &&
        !inputRef.current.contains(e.target) &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSelect = (item) => {
    justSelectedRef.current = true;
    onSelect(item);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative mb-6" ref={inputRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => { onChange(e.target.value); setHighlightedIndex(-1); }}
        onFocus={() => value.trim() && suggestions.length > 0 && setOpen(true)}
        onKeyDown={(e) => {
          if (!open || suggestions.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) => prev < suggestions.length - 1 ? prev + 1 : 0);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => prev > 0 ? prev - 1 : suggestions.length - 1);
          } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            e.preventDefault();
            handleSelect(suggestions[highlightedIndex]);
          }
        }}
        className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white"
      />
      {loading ? (
        <span className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
          Searching...
        </span>
      ) : value.trim().length > 0 ? (
        <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
          {placeholder}
        </span>
      ) : null}
      {open &&
        suggestions.length > 0 &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] bg-white border-2 border-[#E0E0E0] rounded-xl shadow-lg max-h-[240px] overflow-y-auto"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            {suggestions.map((item, idx) => {
              const isHighlighted = highlightedIndex === idx;
              return (
                <div
                  key={item.id ?? item.procedureId}
                  ref={isHighlighted ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" }) : undefined}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  className={`px-4 py-3 cursor-pointer font-lufga text-sm border-b border-gray-100 last:border-b-0 ${
                    isHighlighted ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]" : "hover:bg-[#F4FBF8]"
                  }`}
                >
                  <span className="font-medium text-[#1B1B1B]">
                    {item.procedureId ?? "—"}
                  </span>
                  <span className="text-gray-500 ml-2 truncate block">
                    {item.procedure ?? ""}
                  </span>
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

function AddProcedureForm({
  procedureId,
  setProcedureId,
  procedure,
  setProcedure,
  specialityId,
  setSpecialityId,
  selectedSpecialty,
  setSelectedSpecialty,
  specialties,
  loadingSpecialties,
  fetchSpecialties,
  isStratification,
  setIsStratification,
  isImplant,
  setIsImplant,
  onSubmit,
  submitting,
}) {
  const handleProcedureSelect = (item) => {
    setProcedureId(item.procedureId ?? "");
    setProcedure((item.procedure ?? "").trim());
    setSpecialityId(item.specialityId ?? "");
    setSelectedSpecialty(
      item.specialityId || item.speciality
        ? {
            id: item.specialityId,
            specialityId: item.specialityId,
            speciality: (item.speciality ?? "").trim(),
          }
        : null
    );
  };

  return (
    <div className="font-lufga">
      <div className="mb-6">
        <SpecialtySelect
          label="Speciality Name"
          options={specialties}
          value={selectedSpecialty}
          onSelect={(s) => {
            setSelectedSpecialty(s);
            setSpecialityId(s?.specialityId ?? s?.id ?? "");
          }}
          loading={loadingSpecialties}
          onOpen={fetchSpecialties}
        />
      </div>
      <div className="relative mb-6">
        <input
          type="text"
          placeholder="Speciality ID"
          value={specialityId}
          onChange={(e) => {
            const v = e.target.value;
            if (/^[a-zA-Z]*$/.test(v)) setSpecialityId(v);
          }}
          className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white"
        />
        {specialityId.length > 0 && (
          <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Speciality ID
          </span>
        )}
      </div>
      <div className="relative mb-6">
        <input
          type="text"
          placeholder="Procedure Name"
          value={procedure}
          onChange={(e) => setProcedure(e.target.value)}
          className="w-full h-[70px] px-6 pr-40 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white"
        />
        {procedure.length > 0 && (
          <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Procedure Name
          </span>
        )}
      </div>
      <ProcedureIdAutocomplete
        value={procedureId}
        onChange={(v) => {
          if (/^[a-zA-Z0-9]*$/.test(v)) setProcedureId(v);
        }}
        onSelect={handleProcedureSelect}
        placeholder="Procedure ID"
      />
      <div className="relative mb-6">
        <select
          value={isStratification ? "true" : "false"}
          onChange={(e) => setIsStratification(e.target.value === "true")}
          className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
        >
          <option value="false">False</option>
          <option value="true">True</option>
        </select>
        <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
          Stratification
        </span>
      </div>
      <div className="relative mb-6">
        <select
          value={isImplant ? "true" : "false"}
          onChange={(e) => setIsImplant(e.target.value === "true")}
          className="w-full h-[70px] px-6 pr-28 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
        >
          <option value="false">False</option>
          <option value="true">True</option>
        </select>
        <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
          Implant
        </span>
      </div>
      <button
        disabled={submitting}
        onClick={onSubmit}
        className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Adding..." : "Add Procedure"}
      </button>
    </div>
  );
}

function EditProcedureForm({
  procedureId,
  procedure,
  setProcedure,
  specialityId,
  setSpecialityId,
  selectedSpecialty,
  setSelectedSpecialty,
  specialties,
  loadingSpecialties,
  fetchSpecialties,
  isStratification,
  setIsStratification,
  isImplant,
  setIsImplant,
  onSubmit,
  submitting,
  hasChanges,
}) {
  return (
    <div className="font-lufga">
      <div className="w-full h-[70px] px-6 border-2 border-solid border-[#E0E0E0] rounded-[40px] flex items-center bg-gray-100 font-lufga text-[16px] mb-6 text-[#1B1B1B]">
        {specialityId || "—"}
      </div>
      <div className="mb-6">
        <SpecialtySelect
          label="Speciality Name"
          options={specialties}
          value={selectedSpecialty}
          onSelect={(s) => {
            setSelectedSpecialty(s);
            setSpecialityId(s?.specialityId ?? s?.id ?? "");
          }}
          loading={loadingSpecialties}
          onOpen={fetchSpecialties}
        />
      </div>
      <div className="w-full h-[70px] px-6 border-2 border-solid border-[#E0E0E0] rounded-[40px] flex items-center bg-gray-100 font-lufga text-[16px] mb-6 text-[#1B1B1B]">
        {procedureId || "—"}
      </div>
      <div className="relative mb-6">
        <input
          type="text"
          placeholder="Procedure Name"
          value={procedure}
          onChange={(e) => setProcedure(e.target.value)}
          className="w-full h-[70px] px-6 pr-40 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white"
        />
        {procedure.length > 0 && (
          <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Procedure Name
          </span>
        )}
      </div>
      <div className="relative mb-6">
        <select
          value={isStratification ? "true" : "false"}
          onChange={(e) => setIsStratification(e.target.value === "true")}
          className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
        >
          <option value="false">False</option>
          <option value="true">True</option>
        </select>
        <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
          Stratification
        </span>
      </div>
      <div className="relative mb-6">
        <select
          value={isImplant ? "true" : "false"}
          onChange={(e) => setIsImplant(e.target.value === "true")}
          className="w-full h-[70px] px-6 pr-28 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
        >
          <option value="false">False</option>
          <option value="true">True</option>
        </select>
        <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
          Implant
        </span>
      </div>
      <button
        disabled={submitting || !hasChanges}
        onClick={onSubmit}
        className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Updating..." : "Save Changes"}
      </button>
    </div>
  );
}

function SpecialtySelect({
  label,
  options,
  value,
  onSelect,
  loading,
  onOpen,
  inputClass = "w-full h-[70px] px-6 border-2 border-solid border-[#E0E0E0] rounded-[40px]",
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (
        triggerRef.current &&
        dropdownRef.current &&
        !triggerRef.current.contains(e.target) &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => {
    if (open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + 8,
        left: r.left,
        width: r.width,
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 50);
    if (!open) { setSearchQuery(""); setHighlightedIndex(-1); }
  }, [open]);

  const safe = Array.isArray(options) ? options : [];
  const q = normalizeSpecialitySearch(searchQuery);
  const filtered = q
    ? safe.filter((o) => {
        const label = normalizeSpecialitySearch(o.speciality ?? o.name ?? "");
        return label.includes(q);
      })
    : safe;

  const handleClick = () => {
    setOpen(!open);
    if (!open && onOpen) onOpen();
  };

  const labelKey = "speciality";
  const displayVal = value ? (value[labelKey] ?? value.speciality ?? value.name ?? "") : "";

  const dropdownEl = open && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border-2 border-[#CFCFCF] rounded-xl shadow-lg z-[9999] max-h-[280px] min-h-[80px] flex flex-col overflow-hidden"
      style={{ top: pos.top, left: pos.left, width: pos.width }}
    >
      <div className="sticky top-0 bg-white border-b border-gray-200 p-2 z-10 shrink-0">
        <input
          ref={searchRef}
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setHighlightedIndex(-1); }}
          onKeyDown={(e) => {
            if (filtered.length === 0) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightedIndex((prev) => prev < filtered.length - 1 ? prev + 1 : 0);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightedIndex((prev) => prev > 0 ? prev - 1 : filtered.length - 1);
            } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
              e.preventDefault();
              onSelect(filtered[highlightedIndex]);
              setOpen(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder="Search..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5AC69F]"
        />
      </div>
      <div className="overflow-y-auto flex-1 min-h-0">
        {loading && (
          <div className="p-3 text-sm text-center text-gray-500">Loading...</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-3 text-sm text-center text-gray-500">No results</div>
        )}
        {!loading &&
          filtered.map((item, idx) => {
            const isKeyboardHighlighted = highlightedIndex === idx;
            const isSelected = isSameSpecialtyOption(value, item);
            let rowClass =
              "px-4 py-2 cursor-pointer font-lufga text-[14px] border-b border-gray-100 last:border-b-0 ";
            if (isKeyboardHighlighted) {
              rowClass +=
                "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F] ";
            } else if (isSelected) {
              rowClass += "bg-[#E8F5EE] font-semibold text-[#0d5c3d] ";
            } else {
              rowClass += "hover:bg-[#F4FBF8] ";
            }
            return (
              <div
                key={item.id ?? item.specialityId ?? item.speciality ?? Math.random()}
                ref={
                  isKeyboardHighlighted
                    ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
                    : undefined
                }
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={rowClass}
              >
                {item[labelKey] ?? item.speciality ?? item.name ?? ""}
              </div>
            );
          })}
      </div>
    </div>
  );

  return (
    <div ref={triggerRef}>
      <div
        onClick={handleClick}
        className={`flex items-center gap-2 bg-white cursor-pointer font-lufga text-[16px] ${inputClass}`}
      >
        <span className={`flex-1 min-w-0 truncate text-gray-700 ${!displayVal ? "text-gray-400" : ""}`}>
          {displayVal || label}
        </span>
        {displayVal && (
          <span className="font-lufga text-[13px] text-gray-400 pointer-events-none shrink-0">
            {label}
          </span>
        )}
        <img
          src={downIcon}
          alt="down"
          className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>
      {open && createPortal(dropdownEl, document.body)}
    </div>
  );
}
